import type { ReactNode } from 'react'
import { useI18n } from '../../../i18n'
import { normalizeStability, stabilityColor } from '../../../utils/operationalUx'
import type { UnifiedExecutiveContext } from '../../../types/unifiedExecutiveContext'
import OperationalPressureIndicators from './OperationalPressureIndicators'
import OperationalRuntimeTimeline from './OperationalRuntimeTimeline'
import OperationalBlastRadiusPanel from './OperationalBlastRadiusPanel'
import OperationalStorytellingPanel from './OperationalStorytellingPanel'
import OperationalRecommendationsPanel from './OperationalRecommendationsPanel'
import OperationalWorkflowStatePanel from './OperationalWorkflowStatePanel'
import styles from '../../../styles/enterprise/EnterpriseOperationalUx.module.scss'

export type ExecutiveContextPanelSections = {
  pressure?: boolean
  stability?: boolean
  storytelling?: boolean
  runtime?: boolean
  blast?: boolean
  workflows?: boolean
  recommendations?: boolean
  metrics?: boolean
}

type Props = {
  context: UnifiedExecutiveContext | null
  title?: string
  mode?: 'compact' | 'full'
  sections?: ExecutiveContextPanelSections
  onClose?: () => void
  footerActions?: ReactNode
  emptyMessage?: string
  dark?: boolean
  showHeader?: boolean
}

const DEFAULT_SECTIONS: ExecutiveContextPanelSections = {
  pressure: true,
  stability: true,
  storytelling: true,
  runtime: true,
  blast: true,
  workflows: true,
  recommendations: true,
  metrics: true,
}

export default function ExecutiveOperationalContextPanel({
  context,
  title,
  mode = 'compact',
  sections: sectionsProp,
  onClose,
  footerActions,
  emptyMessage,
  dark = true,
  showHeader = true,
}: Props) {
  const { t } = useI18n()
  const sections = { ...DEFAULT_SECTIONS, ...sectionsProp }

  if (!context || (!context.hasEnoughHistory && context.scope !== 'asset' && context.scope !== 'flow')) {
    return (
      <p className={styles.emptyState}>
        {emptyMessage ||
          t(
            'enterpriseOperationalUx.noExecutiveBaseline',
            'No executive baseline established yet. Run operational recompute when executive governance is enabled.',
          )}
      </p>
    )
  }

  const displayTitle = title || context.title
  const stability = normalizeStability(context.stability?.level)

  return (
    <div
      className={`${styles.contextPanel} ${dark ? styles.contextPanelDark : ''}`}
      data-mode={mode}
    >
      {showHeader ? (
        <header className={styles.contextHeader}>
          <h4>{displayTitle}</h4>
          {onClose ? (
            <button type="button" className={styles.badgeWarn} onClick={onClose} style={{ border: 'none', cursor: 'pointer' }}>
              {t('common.close', 'Close')}
            </button>
          ) : null}
        </header>
      ) : null}

      {sections.pressure ? <OperationalPressureIndicators pressure={context.pressure} density="compact" /> : null}

      {sections.stability && context.stability?.level ? (
        <dl className={styles.metricList}>
          <div className={styles.metricRow}>
            <dt>{t('enterpriseOperationalUx.certificationStability', 'Certification stability')}</dt>
            <dd style={{ color: stabilityColor(stability) }}>{context.stability.level || '—'}</dd>
          </div>
          {context.trajectory ? (
            <div className={styles.metricRow}>
              <dt>{t('enterpriseOperationalUx.readinessTrajectory', 'Readiness trajectory')}</dt>
              <dd>{context.trajectory}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      {sections.metrics ? (
        <dl className={styles.metricList}>
          <div className={styles.metricRow}>
            <dt>{t('executiveGovernance.degradedControls', 'Degraded controls')}</dt>
            <dd>{context.degradedControls}</dd>
          </div>
          <div className={styles.metricRow}>
            <dt>{t('executiveGovernance.blockers', 'Blockers')}</dt>
            <dd>{context.blockers}</dd>
          </div>
          <div className={styles.metricRow}>
            <dt>{t('enterpriseOperationalUx.staleEvidence', 'Stale evidence')}</dt>
            <dd>{context.staleEvidence}</dd>
          </div>
        </dl>
      ) : null}

      {sections.storytelling ? (
        <OperationalStorytellingPanel
          storytelling={context.storytelling}
          explanations={context.explanations}
        />
      ) : null}

      {sections.blast ? (
        <OperationalBlastRadiusPanel hits={context.blastRadius.hits} mode={mode === 'full' ? 'expanded' : 'compact'} />
      ) : null}

      {sections.runtime ? (
        <OperationalRuntimeTimeline
          events={context.runtimeEvents}
          mode={mode === 'full' ? 'full' : 'compact'}
          maxItems={mode === 'full' ? 12 : 6}
        />
      ) : null}

      {sections.workflows ? (
        <OperationalWorkflowStatePanel workflows={context.workflows} compact={mode === 'compact'} />
      ) : null}

      {sections.recommendations ? (
        <OperationalRecommendationsPanel recommendations={context.recommendations} />
      ) : null}

      {footerActions ? <div className={styles.cardActions}>{footerActions}</div> : null}
    </div>
  )
}
